
import AppConfig from '@/AppConfig';
import type { CardPaymentMethod, PaymentMethod } from '@/classes/stripe';
import PaymentMethodEdit from '@/components/Modals/Account/PaymentMethodEdit.vue';
import * as Sentry from '@sentry/vue';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import Icon from '@/components/Icons/Icon.vue';
import Alert from '@/components/Alert.vue';
import { isFeatureEnabled } from '@/Services/FeatureFlagService';
import { optimizelyFeatureFlags } from '@/plugins/optimizely-feature-flags';
import store from '@/store';

@Component({
  components: {
    PaymentMethodEdit,
    Icon,
    Alert,
  },
})
export default class PaymentMethodSummary extends Vue {
  isDialogShown = false;

  defaultPaymentMethod: PaymentMethod = {} as any;

  isPaymentMethodsLoading = false;

  isPaymentMethodsError = false;

  tryAgainPaymentMethod = false;

  @Prop({
    default: false,
  })
  isTeamPaymentMethodInvoice!: boolean;

  @Emit()
  updatePaymentAddress(): boolean {
    return true;
  }

  async created(): Promise<void> {
    await this.loadPaymentMethods();
  }

  async mounted(): Promise<void> {
    if (this.$route.query.returningFromPaypal === 'true' && this.$route.query.step === '3') {
      // Clean up the URL
      await this.$router.replace({
        query: {
          ...this.$route.query,
          returningFromPaypal: undefined,
          step: undefined,
          setup_intent: undefined,
          setup_intent_client_secret: undefined,
        },
      });
    }
  }

  async loadPaymentMethods(tryAgain?: boolean): Promise<any> {
    if (tryAgain) {
      this.isPaymentMethodsError = false;
      this.tryAgainPaymentMethod = true;
    } else {
      this.isPaymentMethodsLoading = true;
    }

    return axios
      .get<PaymentMethod>(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods/default')
      .then((response) => {
        this.defaultPaymentMethod = response.data;
        this.isPaymentMethodsError = false;
        this.tryAgainPaymentMethod = false;
      })
      .catch((error: AxiosError) => {
        Sentry.captureException(error);
        this.isPaymentMethodsError = true;
        this.tryAgainPaymentMethod = false;
      })
      .finally(() => {
        this.isPaymentMethodsLoading = false;
      });
  }

  get getPaymentMethodTitle(): string {
    if (this.isTeamPaymentMethodInvoice) {
      return this.$t('fields.checkout.payment_methods.send_invoice').toString();
    }
    switch (this.defaultPaymentMethod?.type) {
      case 'card':
        return this.$t('fields.checkout.payment_methods.card').toString();
      case 'sepa_debit':
        return this.$t('fields.checkout.payment_methods.sepa_debit').toString();
      case 'paypal':
        return this.$t('fields.checkout.payment_methods.paypal').toString();
      default:
        return this.$t('fields.checkout.payment_methods.send_invoice').toString();
    }
  }

  get isPaymentMethodPaypal(): boolean {
    if (this.getPaymentMethodTitle === this.$t('fields.checkout.payment_methods.paypal').toString()) {
      return true;
    }
    return false;
  }

  get paymentIconName(): string | boolean {
    // currently we don't display any icons for all invoice types like bank transfer or sepa
    if (this.isTeamPaymentMethodInvoice) {
      // could be 'pay-sepa-system' if the method type is provided but is currently omitted
      return false;
    }

    if (this.defaultPaymentMethod?.type === 'paypal') {
      return 'pay-paypal-system';
    }

    if (this.defaultPaymentMethod?.type === 'card') {
      const icons: { [key: string]: string } = {
        mastercard: 'pay-mastercard-system',
        visa: 'pay-visa-system',
        amex: 'pay-amex-system',
        sepa_debit: 'pay-direct-debit-system',
      };
      return icons[(this.defaultPaymentMethod as CardPaymentMethod).brand];
    }

    return false;
  }

  lastTwoDigitsOfYear(year: string | number): number {
    return Number(year) % 100;
  }

  get isPaymentMethodExpired(): boolean {
    if (this.isTeamPaymentMethodInvoice) {
      return false;
    }
    const paymentMethod = this.defaultPaymentMethod as CardPaymentMethod;
    return paymentMethod?.exp_month < dayjs().format('M') && paymentMethod?.exp_year < dayjs().format('YYYY');
  }

  get isThereAnEasierPaymentMethod(): boolean {
    return ['paypal', 'card'].includes(this.defaultPaymentMethod?.type) === false;
  }
  get isFeatureCNV2992Enabled(): boolean {
    return isFeatureEnabled(
      optimizelyFeatureFlags,
      'cnv-2992_change_payment_method_to_paypal',
      store.state?.auth?.user?.id.toString()
    );
  }
}
